import React, { useState, useEffect } from "react";
import { CircularProgress, Typography } from "@mui/material";
import { getProfileStatusLogs } from "utils/enduserUtils";
import { formatDateTime } from "utils/globalsUtils";
import { StatusLog, ProfileStatus } from "models/profile/Profile";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

interface StatusLogsProps {
  profileId: string;
}

export const StatusLogs: React.FC<StatusLogsProps> = ({ profileId }) => {
  const [logs, setLogs] = useState<StatusLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const response = await getProfileStatusLogs(profileId);
        setLogs(response);
      } catch (err) {
        setError("Failed to load status logs");
        console.error("Error fetching status logs:", err);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchLogs();
    }
  }, [profileId]);

  // Helper function to convert status ID to readable name using ProfileStatus enum
  const getStatusName = (statusId: ProfileStatus): string => {
    return ProfileStatus[statusId] || `Status ${statusId}`;
  };

  const columns: GridColDef[] = [
    { 
      field: 'changedAt', 
      headerName: 'Date', 
      width: 180,
      renderCell: (params) => formatDateTime(params.value)
    },
    { 
      field: 'oldStatusId', 
      headerName: 'Previous Status', 
      width: 150,
      renderCell: (params) => params.value ? getStatusName(params.value) : 'N/A'
    },
    { 
      field: 'newStatusId', 
      headerName: 'New Status', 
      width: 150,
      renderCell: (params) => params.value ? getStatusName(params.value) : 'N/A'
    },
    { 
      field: 'reason', 
      headerName: 'Reason', 
      width: 300,
      renderCell: (params) => params.value || 'No reason provided'
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Typography color="error">{error}</Typography>
    );
  }

  if (logs.length === 0) {
    return (
      <Typography>No status logs found for this profile.</Typography>
    );
  }

  return (
    <DataGridPro
      rows={logs}
      columns={columns}
      loading={loading}
      disableRowSelectionOnClick
      density="compact"
      initialState={{
        sorting: {
          sortModel: [{ field: 'changedAt', sort: 'desc' }],
        },
      }}
    />
  );
}; 